import { asyncComputed } from './asyncComputed.js'
import { computed, ref, watch, watchEffect } from 'vue'
import { debounce } from './debounce.js'
import { packQuery, unpackQuery } from '@/utils/filters'

const PAGE_SIZE = 20

export function createScreener(searchFunc, resultProperty = 'results', options = {}) {
    // search criteria
    const fixedCriteria = structuredClone(options?.fixedCriteria ?? [])
    const pageSize = ref(options.pageSize ?? PAGE_SIZE)
    const prefix = options.prefix ?? ''
    const _query = ref(options.query ?? null)

    // underlying values
    const criteria = ref(structuredClone(options?.criteria ?? []))
    const pagination = ref({ offset: 0, limit: pageSize.value })
    const sort = ref({
        field: options.sort?.field ?? null,
        direction: options.sort?.direction ?? null,
    })
    const text = ref(options?.text ?? '')

    function buildCriteria() {
        const c = [...fixedCriteria, ...criteria.value]

        const t = text.value.trim()

        if (t.length > 0) {
            c.push({ name: '_text', value: t })
        }

        return c
    }

    // debounce criteria to limit updates
    const debouncedCriteria = ref([])

    function updateDebouncedCriteria() {
        const c = [...criteria.value]

        const t = text.value.trim()

        if (t.length > 0) {
            c.push({ name: '_text', value: t })
        }

        debouncedCriteria.value = c
    }

    function initializeDebouncer(immediate) {
        const debounced = debounce(updateDebouncedCriteria, 500)

        watch([criteria, text], debounced, { immediate })
    }

    // facets fetching
    const facetsContent = asyncComputed(async signal => {
        return (await searchFunc(fixedCriteria, { offset: 0, limit: 0 }, null, true, signal)).facets
    }, null)

    // results fetching
    const resultsContent = asyncComputed(signal => {
        if (!facetsContent.value || _query.value) {
            return null
        }

        return searchFunc([...fixedCriteria, ...debouncedCriteria.value], pagination.value, sort.value, false, signal)
    }, null)

    // caching
    const count = computed(() => resultsContent.value?.meta?.resultCount ?? 0)
    const facets = computed(() => facetsContent.value ?? [])
    const page = computed({
        get: () => pagination.value.offset / pageSize.value + 1,
        set: p => (pagination.value.offset = (p - 1) * pageSize.value),
    })

    const results = computed(() => resultsContent.value?.[resultProperty] ?? [])

    watchEffect(() => {
        if (resultsContent.value === null) {
            return
        }

        if (pagination.value.offset >= count.value) {
            pagination.value.offset = Math.floor(count.value / pageSize.value) * pageSize.value
        }
    })

    if (_query.value) {
        let watcher = watch(facetsContent, () => {
            if (facetsContent.value === null) {
                return
            }

            criteria.value = unpackQuery(facets.value, _query.value)
            text.value = _query.value['_text'] ?? ''

            updateDebouncedCriteria()

            if (_query.value.page) {
                page.value = parseInt(_query.value[prefix + 'page']) || 1
            }

            sort.value.field = _query.value[prefix + 'sort.field'] ?? sort.value.field
            sort.value.direction = _query.value[prefix + 'sort.direction'] ?? sort.value.direction

            _query.value = null

            initializeDebouncer(false)

            watcher()
        })
    } else {
        initializeDebouncer(true)
    }

    // querystring building

    const query = computed(() => {
        if (_query.value) {
            return _query.value
        }

        const result = packQuery(criteria.value)

        if (page.value !== 1) {
            result[prefix + 'page'] = page.value
        }
        if ((sort.value?.field ?? null) !== (options.sort?.field ?? null)) {
            result[prefix + 'sort.field'] = sort.value?.field ?? null
        }

        if ((sort.value?.direction ?? null) !== (options.sort?.direction ?? null)) {
            result[prefix + 'sort.direction'] = sort.value?.direction ?? null
        }

        if (text.value?.trim()) {
            result['_text'] = text.value.trim()
        }

        return result
    })

    // this is the interface we expose to callers
    return {
        get count() {
            return count.value
        },
        get criteria() {
            return criteria.value
        },
        set criteria(c) {
            criteria.value = c
        },
        get facets() {
            return facets.value
        },
        get page() {
            return page.value
        },
        set page(p) {
            page.value = p
        },
        get pageSize() {
            return pagination.value.limit
        },
        set pageSize(value) {
            pagination.value.limit = value
        },
        get query() {
            return query.value
        },
        get ready() {
            return resultsContent.value !== null
        },
        get sort() {
            return sort.value
        },
        set sort(s) {
            sort.value = s
        },
        get text() {
            return text.value
        },
        set text(t) {
            text.value = t ?? ''
        },
        get [resultProperty]() {
            return results.value
        },
        get updating() {
            return resultsContent.updating
        },
        update: () => {
            debouncedCriteria.value = buildCriteria()
        },
    }
}
